import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Tag, Plus, Loader2, Trash2, Power, Link2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { code: '', type: 'percent', value: '', min_subtotal: '', max_uses: '', expires_at: '', description: '' };

export const VendorVouchersTab = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchVouchers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/vendors/me/vouchers`, authHeaders);
      setVouchers(r.data || []);
    } catch {
      setVouchers([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchVouchers(); }, [fetchVouchers]);

  const createVoucher = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/vendors/me/vouchers`, {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value: parseFloat(form.value),
        min_subtotal: form.min_subtotal ? parseFloat(form.min_subtotal) : 0,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        description: form.description || null,
      }, authHeaders);
      toast.success(T('Code promo créé ✓', 'Promo code created ✓'));
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      fetchVouchers();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur de création', 'Creation error'));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (v) => {
    const newStatus = v.status === 'active' ? 'disabled' : 'active';
    try {
      await axios.put(`${API}/vendors/me/vouchers/${v.id}`, { status: newStatus }, authHeaders);
      setVouchers((prev) => prev.map((x) => (x.id === v.id ? { ...x, status: newStatus } : x)));
      toast.success(newStatus === 'active' ? T('Code activé', 'Code enabled') : T('Code désactivé', 'Code disabled'));
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur', 'Error'));
    }
  };

  const shareLink = (v) => {
    const url = `${window.location.origin}/?promo=${encodeURIComponent(v.code)}`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success(T('Lien de partage copié ! Le code s\'appliquera automatiquement au panier.', 'Share link copied! The code will apply automatically to the cart.')),
      () => toast.info(url)
    );
  };

  const deleteVoucher = async (v) => {
    if (!window.confirm(T('Supprimer ce code promo ?', 'Delete this promo code?'))) return;
    try {
      await axios.delete(`${API}/vendors/me/vouchers/${v.id}`, authHeaders);
      setVouchers((prev) => prev.filter((x) => x.id !== v.id));
      toast.success(T('Code supprimé', 'Code deleted'));
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur', 'Error'));
    }
  };

  return (
    <Card data-testid="vendor-vouchers-tab">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="flex items-center gap-2">
          <Tag className="w-5 h-5 text-[#0F4C5C]" />
          {T('Mes codes promo', 'My promo codes')}
        </CardTitle>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-voucher-btn">
              <Plus className="w-4 h-4 mr-2" />
              {T('Créer un code', 'Create code')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{T('Nouveau code promo', 'New promo code')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={createVoucher} className="space-y-4 mt-2">
              <p className="text-xs text-muted-foreground bg-[#FBF8F3] border border-[#0F4C5C]/10 rounded-lg p-3">
                {T('Ce code ne s\'appliquera qu\'aux articles de votre boutique dans le panier du client.',
                   'This code will only apply to items from your shop in the customer\'s cart.')}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{T('Code', 'Code')} *</Label>
                  <Input
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="ETE2026"
                    className="mt-1 font-mono uppercase"
                    minLength={3}
                    maxLength={32}
                    required
                    data-testid="voucher-code-input"
                  />
                </div>
                <div>
                  <Label>{T('Type', 'Type')}</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-1" data-testid="voucher-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">{T('Pourcentage (%)', 'Percentage (%)')}</SelectItem>
                      <SelectItem value="fixed">{T('Montant fixe (€)', 'Fixed amount (€)')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{form.type === 'percent' ? T('Réduction (%)', 'Discount (%)') : T('Réduction (€)', 'Discount (€)')} *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={form.type === 'percent' ? 100 : undefined}
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="voucher-value-input"
                  />
                </div>
                <div>
                  <Label>{T('Minimum d\'achat (€)', 'Min. purchase (€)')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.min_subtotal}
                    onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })}
                    placeholder="0"
                    className="mt-1"
                    data-testid="voucher-min-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{T('Utilisations max', 'Max uses')}</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    placeholder={T('Illimité', 'Unlimited')}
                    className="mt-1"
                    data-testid="voucher-max-uses-input"
                  />
                </div>
                <div>
                  <Label>{T('Date d\'expiration', 'Expiry date')}</Label>
                  <Input
                    type="datetime-local"
                    value={form.expires_at}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                    className="mt-1"
                    data-testid="voucher-expires-input"
                  />
                </div>
              </div>
              <div>
                <Label>{T('Description (interne)', 'Description (internal)')}</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder={T('Ex : Campagne Instagram été', 'e.g. Summer Instagram campaign')}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {T('Annuler', 'Cancel')}
                </Button>
                <Button type="submit" disabled={saving} className="btn-primary" data-testid="voucher-submit-btn">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {T('Créer le code', 'Create code')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#0F4C5C]" /></div>
        ) : vouchers.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {T('Aucun code promo. Créez-en un à partager avec votre communauté !', 'No promo codes yet. Create one to share with your community!')}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{T('Code', 'Code')}</TableHead>
                <TableHead>{T('Réduction', 'Discount')}</TableHead>
                <TableHead>{T('Utilisations', 'Uses')}</TableHead>
                <TableHead>{T('CA généré', 'Revenue')}</TableHead>
                <TableHead>{T('Clics lien', 'Link clicks')}</TableHead>
                <TableHead>{T('Min. achat', 'Min. purchase')}</TableHead>
                <TableHead>{T('Expire', 'Expires')}</TableHead>
                <TableHead>{T('Statut', 'Status')}</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vouchers.map((v) => (
                <TableRow key={v.id} data-testid={`voucher-row-${v.code}`}>
                  <TableCell className="font-mono font-semibold">{v.code}</TableCell>
                  <TableCell>{v.type === 'percent' ? `-${v.value}%` : `-€${Number(v.value).toFixed(2)}`}</TableCell>
                  <TableCell>{v.usage_count || 0}{v.max_uses ? ` / ${v.max_uses}` : ''}</TableCell>
                  <TableCell data-testid={`voucher-revenue-${v.code}`}>
                    {v.revenue > 0 ? (
                      <span className="font-semibold text-emerald-700">
                        €{Number(v.revenue).toFixed(2)}
                        <span className="font-normal text-xs text-muted-foreground ml-1">
                          ({v.orders_count} {T('cmd', 'ord')})
                        </span>
                      </span>
                    ) : '—'}
                  </TableCell>
                  <TableCell data-testid={`voucher-clicks-${v.code}`}>
                    {v.share_clicks > 0 ? (
                      <span className="font-medium text-[#0F4C5C]">{v.share_clicks}</span>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{v.min_subtotal ? `€${Number(v.min_subtotal).toFixed(2)}` : '—'}</TableCell>
                  <TableCell>{v.expires_at ? new Date(v.expires_at).toLocaleDateString() : '—'}</TableCell>
                  <TableCell>
                    <Badge className={v.status === 'active' ? 'badge-success' : 'badge-warning'}>
                      {v.status === 'active' ? T('Actif', 'Active') : T('Désactivé', 'Disabled')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        title={T('Copier le lien de partage', 'Copy share link')}
                        onClick={() => shareLink(v)}
                        data-testid={`voucher-share-${v.id}`}
                      >
                        <Link2 className="w-4 h-4 text-[#0F4C5C]" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        title={v.status === 'active' ? T('Désactiver', 'Disable') : T('Activer', 'Enable')}
                        onClick={() => toggleStatus(v)}
                        data-testid={`voucher-toggle-${v.id}`}
                      >
                        <Power className={`w-4 h-4 ${v.status === 'active' ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => deleteVoucher(v)}
                        data-testid={`voucher-delete-${v.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
